import { useConfig, useValidation, type Rules } from '@seek/libs-shared';
import { Heading, Stack } from 'braid-design-system';
import { useEffect } from 'react';
import {
  CURRENT_PAGE_DIVERSITY_AND_EQUAL_OPPORTUNITY,
  trackContinuePressed,
  trackDisplayPage,
  trackSkipQuestionPressed,
} from '../../../shared/tracking/writeAReview/trackingEvents';
import { ReviewPageLayoutNoStepper } from '../../components/ReviewPageLayout/ReviewPageLayoutNoStepper';
import type { Rating } from '../../components/StarSelector/StarSelector';
import { DiversityRatingQuestion } from '../../questions/RatingQuesions/DiversityRatingQuestion';
import { DiversityEqualOpptyDetailsQuestion } from '../../questions/SimpleQuestions/DiversityEqualOpptyDetailsQuestion';
import {
  ExperimentTwoProgress,
  type PageProps,
  type ReviewPayload,
} from '../../type';
import { validationRules } from '../../validation/validationRules';

type Props = PageProps & {
  isSubmitting: boolean;
};

export const DiversityEqualOpptyPage = ({
  review,
  reviewFormContext,
  setPage,
  setter,
  pageNumber,
  isSubmitting,
  onSubmit,
}: Props) => {
  const config = useConfig();

  useEffect(() => {
    trackDisplayPage({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_DIVERSITY_AND_EQUAL_OPPORTUNITY,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fields = {
    diversityDescription: review?.diversityDescription,
  };

  const { diversityDescription } = validationRules(config.language);

  const rules = { diversityDescription } as Rules<typeof fields>;

  const {
    getTone,
    getMessage,
    handleValueChangeWithValidation,
    validateWithErrors,
  } = useValidation<ReviewPayload>(fields, rules);

  const handleOnContinue = () => {
    const { isValid } = validateWithErrors();

    if (onSubmit) {
      onSubmit();
    }

    trackContinuePressed({
      config,
      context: reviewFormContext,
      validation: isValid ? 'success' : 'mandatory_questions_not_answered',
      currentPage: CURRENT_PAGE_DIVERSITY_AND_EQUAL_OPPORTUNITY,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });

    if (!isValid) {
      return;
    }

    setPage(ExperimentTwoProgress.WorkingEnvironment);
  };

  const handleOnSkip = () => {
    trackSkipQuestionPressed({
      config,
      context: reviewFormContext,
      currentPage: CURRENT_PAGE_DIVERSITY_AND_EQUAL_OPPORTUNITY,
      variant: reviewFormContext?.variant ?? '1.0.0',
    });

    setPage(ExperimentTwoProgress.WorkingEnvironment);
  };
  const handleOnChange =
    (updateProperty: keyof ReviewPayload) => (value: Rating) => {
      setter({
        ...review,
        [updateProperty]: value,
      });
    };

  return (
    <ReviewPageLayoutNoStepper
      progress={pageNumber}
      onContinue={handleOnContinue}
      onSkip={handleOnSkip}
      isContinueLoading={isSubmitting}
    >
      <Stack space="large">
        <Heading level="1">Diversity and equal opportunity</Heading>
        <DiversityRatingQuestion
          onChange={handleOnChange('diversityRating')}
          value={review?.diversityRating}
          title="Rate diversity and equal opportunity"
          isRequired={true}
        />
        <DiversityEqualOpptyDetailsQuestion
          onChange={handleValueChangeWithValidation({
            setter,
            validatorProperty: 'diversityDescription',
            previousValue: review,
          })}
          value={review?.diversityDescription}
          tone={getTone('diversityDescription')}
          message={getMessage('diversityDescription')}
        />
      </Stack>
    </ReviewPageLayoutNoStepper>
  );
};
